import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { ArrowRight, CheckCircle, Shield, Users, BookOpen, Phone, Eye } from 'lucide-react';

const values = [
  { icon: BookOpen, title: '25 Years of Experience', description: 'Two and a half decades working with individuals and businesses on tax preparation, resolution, and advisory services.' },
  { icon: Users, title: 'Real People, Real Problems', description: 'We\'ve helped thousands of clients navigate IRS notices, unfiled returns, payment plans, and complex tax situations.' },
  { icon: Eye, title: 'Plain-English Guidance', description: 'No jargon, no complicated explanations. We tell you what\'s happening, what your options are, and what we recommend — in language you can understand.' },
  { icon: Shield, title: 'Professional & Confidential', description: 'Your tax situation is personal. We treat every case with the professionalism and confidentiality it deserves.' },
];

const approach = [
  'We don\'t make unrealistic promises about what the IRS will accept',
  'We explain realistic outcomes based on your facts',
  'We give you options, not pressure',
  'We charge fair, transparent fees',
  'We respond to your calls and emails',
  'We treat you like a person, not a case number',
];

export default function About() {
  useSEO({
    title: 'About TaxPro - Answers — 25 Years of Tax Experience',
    description: 'Professional, confidential tax guidance. 25 years helping individuals and businesses with IRS issues, tax preparation, and resolution. No hype, no unrealistic promises.',
  });

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
                About TaxPro - Answers
              </p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
                Why Work With Us
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed">
                With 25 years of experience, we help individuals and business owners understand their tax problems, review their options, and take the next right step. No hype. No scare tactics. Just honest, professional guidance.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.15} className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/8123801/pexels-photo-8123801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional consultation"
                className="w-full h-80 object-cover rounded-lg"
                loading="lazy"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              What Sets Us Apart
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Experience that makes a difference
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {values.map((v, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="bg-white p-8 h-full">
                  <div className="w-11 h-11 rounded-md bg-brand-navy/5 flex items-center justify-center mb-5">
                    <v.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-brand-navy mb-3">{v.title}</h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{v.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Our Approach
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
                Honest guidance you can trust
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-8">
                We've built our practice on doing the right thing for our clients. That means being honest about outcomes, transparent about pricing, and accessible when you need us.
              </p>
              <div className="space-y-3">
                {approach.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="bg-brand-navy rounded-lg p-8 text-white">
                <h3 className="font-heading font-bold text-xl mb-4">Types of Issues We Handle</h3>
                <div className="space-y-3">
                  {[
                    'IRS notices and letters',
                    'Unfiled tax returns',
                    'Tax debt and payment plans',
                    'Penalty and interest relief',
                    'Wage garnishments and bank levies',
                    'Self-employed and business taxes',
                    'Prior-year and amended returns',
                    'Tax planning and strategy',
                  ].map((issue, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                      <span className="text-sm text-slate-300">{issue}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Let's have a conversation
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Whether you have a quick question or a complex tax issue, we're here to help. Start with a call or a question.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Ask a Tax Question - Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
